import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import ArrowBackIosNewOutlined from '@mui/icons-material/ArrowBackIosNewOutlined';
import { useNavigate, useParams } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import Button from '../components/Button';
import PageContainer from '../layouts/PageContainer';
import PageState from '../layouts/PageState';
import { V2_PRIMARY_MAIN } from '../design/muiTheme';
import { V2ApiError } from '../services/client';
import {
  formatCartCustomizationSummary,
  listCartCustomizationAddOns,
} from '../services/commerce';
import { fetchOrderById } from '../services/orders';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';

function formatOrderStatus(raw) {
  const s = String(raw || 'pending').toLowerCase();
  return s
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function statusChipColor(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'delivered' || s === 'completed') return 'success';
  if (s === 'cancelled' || s === 'returned') return 'error';
  if (s === 'out_for_delivery') return 'info';
  if (s === 'confirmed' || s === 'processing') return 'primary';
  return 'default';
}

function formatPlacedAt(order) {
  const raw = order?.timeStamp ?? order?.createdAt;
  if (raw == null) return null;
  const d = new Date(typeof raw === 'number' ? raw : raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPaymentMethod(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'cash_on_delivery' || s === 'cod') return 'Cash on delivery';
  if (s === 'online') return 'Online payment';
  return raw ? String(raw) : '—';
}

function formatPaymentStatus(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'paid') return 'Paid';
  if (s === 'unpaid' || s === 'pending') return 'Unpaid';
  return raw ? formatOrderStatus(raw) : '—';
}

function formatDeliveryAddress(addr) {
  if (!addr || typeof addr !== 'object') return null;
  const name = addr.userName || addr.name;
  const lines = [
    addr.houseDetails,
    addr.streetDetails,
    addr.landmark,
    addr.pincode != null && addr.pincode !== '' ? `PIN ${addr.pincode}` : null,
  ].filter(Boolean);
  const body = lines.join(', ');
  if (name && body) return `${name} — ${body}`;
  if (name) return String(name);
  return body || null;
}

function formatSlot(slot) {
  if (!slot || typeof slot !== 'object') return null;
  if (slot.displayTime) return String(slot.displayTime);
  const a = slot.startTime;
  const b = slot.endTime;
  if (a && b) return `${a} – ${b}`;
  return null;
}

function DetailRow({ label, value, valueProps = {} }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', minWidth: 0 }} {...valueProps}>
        {value}
      </Typography>
    </Box>
  );
}

function OrderLineCard({ line }) {
  const qty = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  const rowPrice = unit * qty;
  const addOns = listCartCustomizationAddOns(line.customizations);
  const compactCustom = formatCartCustomizationSummary(line.customizations);
  const customizationInline =
    addOns.length > 0
      ? addOns.map((a) => (a.additionalPrice > 0 ? `${a.name} (+₹${a.additionalPrice})` : a.name))
      : compactCustom
        ? compactCustom.split(' · ')
        : [];
  const variantBits = [line.variant?.weight ? String(line.variant.weight) : null].filter(Boolean);

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
        <CardMedia
          component="img"
          image={line.imageUrl || noImage}
          alt={line.name || 'Product'}
          sx={{
            width: 88,
            height: 88,
            flexShrink: 0,
            borderRadius: 1,
            objectFit: 'cover',
            bgcolor: 'grey.100',
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, minWidth: 0 }}>
              {line.name || 'Item'}
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} sx={{ flexShrink: 0 }}>
              ₹{rowPrice}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            ₹{unit} × {qty}
          </Typography>
          {variantBits.length ? (
            <Typography variant="caption" color="text.secondary">
              {variantBits.join(' · ')}
            </Typography>
          ) : null}
          {customizationInline.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {customizationInline.join(' · ')}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Card>
  );
}

export default function OrderDetails() {
  const { orderId: orderIdParam } = useParams();
  const navigate = useNavigate();
  const { mobile } = useV2Session();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);

  const load = useCallback(async () => {
    if (!orderIdParam) {
      setError('Missing order id');
      setOrder(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const o = await fetchOrderById(orderIdParam);
      const owner = String(o?.userId ?? '').trim();
      const me = String(mobile ?? '').trim();
      if (me && owner && owner !== me) {
        setError('This order is not linked to your account.');
        setOrder(null);
        return;
      }
      setOrder(o);
    } catch (err) {
      const msg =
        err instanceof V2ApiError
          ? err.message
          : err?.message || 'Could not load this order.';
      setError(msg);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderIdParam, mobile]);

  useEffect(() => {
    load();
  }, [load]);

  const oid = order?.orderId || order?.id || orderIdParam;
  const status = order?.order_status || order?.status || 'pending';
  const products = Array.isArray(order?.products) ? order.products : [];
  const subtotal = Number(order?.subtotal ?? 0);
  const shipping = Number(order?.shippingCost ?? order?.shipping_cost ?? 0);
  const discount = Number(order?.discount ?? 0);
  const total = Number(order?.total ?? order?.order_amount ?? 0);
  const addrText = formatDeliveryAddress(order?.deliveryAddress);
  const store = order?.storeDetails;
  const slotText = formatSlot(order?.timeSlotDetails);

  return (
    <PageContainer>
      <Button
        variant="text"
        size="small"
        startIcon={<ArrowBackIosNewOutlined sx={{ fontSize: 14 }} />}
        onClick={() => navigate(paths.account)}
        sx={{ mb: 1.5, px: 0, minWidth: 0, color: 'text.primary' }}
      >
        Back to profile
      </Button>

      <Typography variant="h5" component="h1" fontWeight={800} gutterBottom>
        Order details
      </Typography>

      <PageState
        loading={loading}
        error={error}
        onRetry={load}
        loadingMessage="Loading order…"
        errorTitle={error || 'Could not load order'}
      >
        {order ? (
          <>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Order ID
                  </Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: V2_PRIMARY_MAIN, wordBreak: 'break-all', lineHeight: 1.25 }}>
                    {oid}
                  </Typography>
                </Box>
                <Chip size="small" color={statusChipColor(status)} label={formatOrderStatus(status)} sx={{ fontWeight: 600 }} />
              </Box>
              <DetailRow label="Placed on" value={formatPlacedAt(order)} />
              <DetailRow label="Delivery type" value={order?.deliveryType} />
            </Card>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Items ({products.length})
            </Typography>
            {products.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No line items were returned for this order.
              </Typography>
            ) : (
              products.map((line, idx) => <OrderLineCard key={String(line?.productId ?? line?.id ?? idx)} line={line} />)
            )}

            {order?.instructions ? (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Instructions
                </Typography>
                <Card
                  elevation={0}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}
                >
                  <Typography variant="body2">{String(order.instructions)}</Typography>
                </Card>
              </>
            ) : null}

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Payment
            </Typography>
            <Card
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}
            >
              <DetailRow label="Method" value={formatPaymentMethod(order?.paymentMethod)} />
              <DetailRow label="Payment status" value={formatPaymentStatus(order?.paymentStatus)} />
              {order?.transactionRef ? <DetailRow label="Transaction ref" value={String(order.transactionRef)} /> : null}
            </Card>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Delivery
            </Typography>
            <Card
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}
            >
              <DetailRow label="Date" value={order?.deliveryDate ? String(order.deliveryDate) : null} />
              <DetailRow label="Time slot" value={slotText} />
              <DetailRow label="Address" value={addrText} valueProps={{ fontWeight: 500, whiteSpace: 'pre-wrap' }} />
              {store?.storeName ? (
                <DetailRow label="Store" value={String(store.storeName)} />
              ) : null}
            </Card>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, mt: 0.5 }}>
              Bill summary
            </Typography>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
              <DetailRow label="Subtotal" value={`₹${subtotal}`} />
              <DetailRow label="Shipping" value={`₹${shipping}`} />
              {discount > 0 ? (
                <DetailRow
                  label={
                    order?.discountType === 'loyalty' || order?.appliedCoupon === 'loyalty'
                      ? 'Naatu points'
                      : order?.appliedCoupon
                        ? `Discount (${order.appliedCoupon})`
                        : 'Discount'
                  }
                  value={`−₹${discount}`}
                  valueProps={{ color: 'success.main' }}
                />
              ) : null}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={800}>
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  ₹{total}
                </Typography>
              </Box>
            </Card>
          </>
        ) : null}
      </PageState>
    </PageContainer>
  );
}
